import React, { useEffect } from "react";
import { z } from "zod";
import { differenceInHours, isValid, parseISO, sub, format } from "date-fns";
import type { RouteComponentProps } from "@gatsbyjs/reach-router";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { navigate } from "utilities/navigate";
import {
  Typography,
  Form,
  Input,
  NativeSelect,
  DateInput,
  Checkbox,
  Button,
  PageLoader,
  Alert,
  toast,
} from "@picklebet/ui";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { verificationService } from "features/account/services/verification";
import { useVerificationFormAttempt } from "features/account/hooks/useVerifications";
import { handleFailedVerificationAttempt } from "features/account/utils/verification";
import { useCustomerLicence } from "@/features/auth/hooks/usePermission";
import { telemetry } from "@/utilities/telemetry";
import { DVSConsent } from "@/features/account/components/verification/DVSConsent";
import { LEGAL_NAME } from "utilities/regex";
import { VERIFICATION_DATA_SOURCES } from "features/account/types/Verification";
import { isValidFutureDate } from "utilities/datetime";

const schema = z.object({
  number: z
    .string()
    .min(1, "A valid Medicare number is required")
    .length(10, "A valid Medicare number is required"),
  dateOfBirth: z
    .string()
    .min(1, "A valid date of birth is required")
    .refine(
      (dob) => {
        const date = parseISO(dob);
        return (
          isValid(date) &&
          differenceInHours(sub(new Date(), { years: 18 }), date) >= 0
        );
      },
      { message: "You must be at least 18 years old" },
    ),
  cardColour: z.string().min(1, "Must be a valid Medicare card colour"),
  nameOnCard: z
    .string()
    .min(1, "Name is a required field")
    .regex(LEGAL_NAME, "Please enter a valid name"),
  nameLine2: z.string().optional(),
  nameLine3: z.string().optional(),
  nameLine4: z.string().optional(),
  expiryDate: z
    .string({ error: "A valid expiry date is required" })
    .refine(isValidFutureDate, {
      message: "Your expiry date is invalid or Medicare card has expired",
    }),
  individualReferenceNumber: z
    .string()
    .min(1, "Individual reference number is required"),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must consent to Picklebet checking the information above.",
  }),
  isMultiRow: z.enum(["yes", "no"]).optional(),
});

type FormValues = z.infer<typeof schema>;

const refNumberOptions = Array.from({ length: 10 }, (_, i) => i + 1).map(
  (i) => ({
    label: i.toString(),
    value: i.toString(),
  }),
);

type MedicareCardProps = {
  prefix?: string;
} & RouteComponentProps;

const MedicareCard = ({ prefix = "/account" }: MedicareCardProps) => {
  const licence = useCustomerLicence();
  const { attempt, isLoading, hasSubmitted, markSubmitted } =
    useVerificationFormAttempt();

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      cardColour: "G",
      individualReferenceNumber: "1",
      isMultiRow: "no",
    },
  });

  const { profile } = useProfile();

  useEffect(() => {
    if (profile) {
      setValue("dateOfBirth", profile.dateOfBirth);
    }
  }, [profile]);

  const consent = watch("consent", false);
  const isMultiRow = watch("isMultiRow", "no");
  const isGreenCard = watch("cardColour", "G") === "G";

  const onSubmit = async ({
    consent,
    isMultiRow: _isMultiRow,
    ...fields
  }: FormValues) => {
    if (!attempt) {
      toast({
        variant: "danger",
        description:
          "Something went wrong. Please refresh and try again or contact support.",
      });
      return;
    }

    const verificationType = VERIFICATION_DATA_SOURCES.MEDICARE_CARD;
    const normalizedFields = {
      ...fields,
      number: fields.number.replace(/\s/g, ""),
      expiryDate: format(
        parseISO(fields.expiryDate),
        fields.cardColour === "G" ? "MM/yyyy" : "dd/MM/yy",
      ),
    };

    const result = await verificationService.submitAttemptData({
      attemptId: attempt.attemptId,
      attemptType: "VIXIdentityDocuments",
      type: "MedicareCard",
      terms: consent,
      fields: normalizedFields,
    });

    if (!result.ok) {
      handleFailedVerificationAttempt(verificationType, licence);
      return;
    }

    telemetry.submittedVerificationForm({
      outcome: "success",
      type: verificationType,
      licence: licence ?? undefined,
    });

    markSubmitted();
    navigate(`${prefix}/verification`);
  };

  if (isLoading || hasSubmitted) return <PageLoader />;

  if (!attempt && !isSubmitting) {
    return (
      <Alert
        color="danger"
        actions={
          <Button
            onClick={() => window.location.reload()}
            size="md"
            variant="danger"
          >
            Refresh
          </Button>
        }
      >
        Unable to load verification option, please refresh to try again.
      </Alert>
    );
  }

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        fixedWidth
        disabled={isSubmitting}
      >
        <Typography variant="h4" noSpacing>
          Medicare Card
        </Typography>

        <Typography variant="body">
          Please enter your identity information, exactly as it appears on your
          Australian Medicare card.
        </Typography>

        <Typography variant={`body`}>
          Don't have a Medicare card? Select from a list of{" "}
          <Link to={`${prefix}/verification`}>Verification options</Link>.
        </Typography>

        <Input
          maxLength={10}
          {...register("number")}
          label={`Medicare Number`}
          errorMessage={errors?.number?.message}
        />

        <NativeSelect
          {...register("individualReferenceNumber")}
          label={`Individual Reference Number`}
          options={refNumberOptions}
          errorMessage={errors?.individualReferenceNumber?.message}
        />

        <NativeSelect
          {...register("isMultiRow")}
          label={`Does your name run across more than one row on the card`}
          options={[
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" },
          ]}
          errorMessage={errors?.isMultiRow?.message}
        />

        <Input
          maxLength={27}
          label={`Name (as it appears on the card)`}
          placeholder={"Exactly as it appears on your card"}
          {...register("nameOnCard")}
          errorMessage={errors?.nameOnCard?.message}
        />

        {isMultiRow === "yes" && (
          <>
            <Input
              maxLength={27}
              label={`Name Line 2`}
              {...register("nameLine2")}
              errorMessage={errors?.nameLine2?.message}
            />

            <Input
              maxLength={27}
              label={`Name Line 3`}
              {...register("nameLine3")}
              errorMessage={errors?.nameLine3?.message}
            />

            <Input
              maxLength={27}
              label={`Name Line 4`}
              {...register("nameLine4")}
              errorMessage={errors?.nameLine4?.message}
            />
          </>
        )}

        <NativeSelect
          {...register("cardColour")}
          label={`Card Colour`}
          options={[
            { label: "Green", value: "G" },
            { label: "Blue", value: "B" },
            { label: "Yellow", value: "Y" },
          ]}
          errorMessage={errors?.cardColour?.message}
        />

        <Controller
          control={control}
          name={"expiryDate"}
          render={({ field: { ref: _, ...field } }) => (
            <DateInput
              id={"expiryDate"}
              {...field}
              errorMessage={errors.expiryDate?.message}
              hideDays={isGreenCard}
              label={`Card Expiry`}
            />
          )}
        />

        <Controller
          control={control}
          name={"dateOfBirth"}
          render={({ field: { ref: _, ...field } }) => (
            <DateInput
              id={"dateOfBirth"}
              {...field}
              errorMessage={errors.dateOfBirth?.message}
              label={`Date of Birth`}
            />
          )}
        />

        <Controller
          control={control}
          render={({ field: { onChange, value: _, ...field } }) => (
            <Checkbox
              id={`consent`}
              label={DVSConsent}
              checked={consent}
              onCheckedChange={(checked) => onChange?.(checked === true)}
              hasError={!!errors?.consent?.message}
              {...field}
            />
          )}
          name={`consent`}
        />

        <Button type={`submit`} loading={isSubmitting}>
          Verify Card
        </Button>
      </Form>
    </>
  );
};

export { MedicareCard };
